import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import { ChevronLeft, ChevronRight } from "lucide-react";
import useMeasure from "react-use-measure";
import { useAutoScrollOnHover } from "replo-utils/react/use-auto-scroll-on-hover";

const IFRAME_BASE_WIDTH = 1440;
const SCROLLBAR_HIDE_PX = 20;
const IFRAME_HEIGHT = 3000;
const DEFAULT_IFRAME_SCALE = 0.208;

const FRAME_GUTTER_PX = 12;
const PEEK_WIDTH_PX = 16;
const SIDE_OFFSET_Y_PX = 8;
const SIDE_OPACITY = 0.5;

interface SitePreviewCardProps {
  styleId: string;
  styleName: string;
  fontUrl: string | null;
  fontFamily: string;
  subtleBackgroundColor: string | null;
  pages: string[];
  pageHtmlByRoute: Record<string, string>;
  isSelected: boolean;
  isLoading?: boolean;
  onSelect: () => void;
}

export function SitePreviewCard({
  styleId,
  styleName,
  fontUrl,
  fontFamily,
  subtleBackgroundColor,
  pages,
  pageHtmlByRoute,
  isSelected,
  isLoading = false,
  onSelect,
}: SitePreviewCardProps) {
  const isMobile = useIsMobile();
  const [currentPageIndex, setCurrentPageIndex] = React.useState(0);
  const safePages = Array.isArray(pages) ? pages : [];

  const hasMultiplePages = safePages.length > 1;
  const fontFaceFamilyName = React.useMemo(
    () => `ReploStarterSite_${styleId}`,
    [styleId],
  );

  const iframeContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [measureRef, frameBounds] = useMeasure({ offsetSize: true });
  const frameRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      iframeContainerRef.current = node;
      measureRef(node);
    },
    [measureRef],
  );

  const { startScrolling, stopScrolling } = useAutoScrollOnHover({
    getIframe: () => {
      return (
        iframeContainerRef.current?.querySelector<HTMLIFrameElement>(
          `[data-page-index="${currentPageIndex}"] iframe`,
        ) ?? null
      );
    },
  });

  const handlePrevPage = () => {
    if (safePages.length === 0) {
      return;
    }
    stopScrolling();
    setCurrentPageIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    if (safePages.length === 0) {
      return;
    }
    stopScrolling();
    setCurrentPageIndex((prev) => Math.min(safePages.length - 1, prev + 1));
  };
  const iframeScale =
    frameBounds.width > 0
      ? frameBounds.width / IFRAME_BASE_WIDTH
      : DEFAULT_IFRAME_SCALE;

  const getPageTransform = (index: number) => {
    const delta = index - currentPageIndex;
    if (delta === 0) {
      return "translate(0, 0)";
    }
    const xOffset = delta * 100;
    const peekInset = delta > 0 ? -PEEK_WIDTH_PX : PEEK_WIDTH_PX;
    return `translate(calc(${xOffset}% + ${peekInset}px), ${SIDE_OFFSET_Y_PX}px)`;
  };

  const getPageOpacity = (index: number) => {
    return index === currentPageIndex ? 1 : SIDE_OPACITY;
  };

  return (
    <div
      onClick={onSelect}
      role="button"
      tabIndex={0}
      aria-label={`Select ${styleName} style`}
      className={twMerge(
        "border-0.5 border-border flex flex-col gap-0 rounded-xl p-0 shadow-md overflow-hidden cursor-pointer w-full md:w-75 shrink-0 transition-all duration-200",
        isSelected && "border-[0.8px] border-black",
      )}
    >
      <style>
        {fontUrl
          ? `@font-face {
  font-family: "${fontFaceFamilyName}";
  src: url("${fontUrl}") format("woff2");
  font-display: swap;
}`
          : ""}
      </style>

      <div
        className="flex flex-col gap-0 px-4 pt-4 pb-0 h-75 overflow-visible relative"
        style={{
          backgroundColor: subtleBackgroundColor ?? "#FFFFFF",
        }}
      >
        {isLoading ? (
          <Skeleton className="w-full h-full rounded-t-lg shadow-2xl" />
        ) : (
          <>
            <div
              ref={frameRef}
              onMouseEnter={isMobile ? undefined : startScrolling}
              onMouseLeave={isMobile ? undefined : stopScrolling}
              className="relative h-full rounded-t-lg overflow-visible shadow-2xl mx-auto"
              style={{
                width: `calc(100% - ${FRAME_GUTTER_PX}px)`,
                backgroundColor: subtleBackgroundColor ?? "#FFFFFF",
              }}
            >
              {safePages.map((pageRoute, index) => {
                const isActive = index === currentPageIndex;
                if (isMobile && !isActive) {
                  return null;
                }
                return (
                  <div
                    key={pageRoute}
                    data-page-index={index}
                    className={twMerge(
                      "absolute inset-0 w-full h-full transition-[transform,opacity] duration-300 ease-out",
                      isActive && "z-10",
                    )}
                    style={{
                      transform: isMobile ? undefined : getPageTransform(index),
                      opacity: isMobile ? undefined : getPageOpacity(index),
                    }}
                  >
                    <div
                      className="w-full h-full rounded-t-lg overflow-hidden shadow-lg"
                      style={{
                        backgroundColor: subtleBackgroundColor ?? "#FFFFFF",
                      }}
                    >
                      <iframe
                        title={`${styleName} preview ${index + 1}`}
                        srcDoc={pageHtmlByRoute[pageRoute] ?? ""}
                        className="origin-top-left pointer-events-none"
                        style={{
                          width: IFRAME_BASE_WIDTH + SCROLLBAR_HIDE_PX,
                          height: IFRAME_HEIGHT,
                          transform: `scale(${iframeScale})`,
                          backgroundColor: subtleBackgroundColor ?? "#FFFFFF",
                        }}
                        sandbox="allow-same-origin"
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="absolute bottom-0 left-0 right-0 z-10 h-16 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none" />
          </>
        )}
      </div>

      <div className="flex items-center justify-between p-3 bg-white relative z-10">
        <span
          className="header-h2 font-bold"
          style={{
            fontFamily: fontUrl
              ? `${fontFaceFamilyName}, ${fontFamily}, Inter, sans-serif`
              : `${fontFamily}, Inter, sans-serif`,
          }}
        >
          {styleName}
        </span>

        {hasMultiplePages && (
          <div className="flex items-center gap-2">
            <Tooltip content="Previous page" triggerAsChild>
              <Button
                variant="secondary"
                size={isMobile ? "base" : "sm"}
                icon
                disabled={currentPageIndex === 0}
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrevPage();
                }}
              >
                <ChevronLeft
                  className={isMobile ? "size-5" : "size-4"}
                  strokeWidth={2}
                />
              </Button>
            </Tooltip>
            <Tooltip content="Next page" triggerAsChild>
              <Button
                variant="secondary"
                size={isMobile ? "base" : "sm"}
                icon
                disabled={currentPageIndex === safePages.length - 1}
                onClick={(e) => {
                  e.stopPropagation();
                  handleNextPage();
                }}
              >
                <ChevronRight
                  className={isMobile ? "size-5" : "size-4"}
                  strokeWidth={2}
                />
              </Button>
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
}
